/**
 * CR-7732: the haulier's own figures on a call-off — price, pickup date,
 * agreed delivery date and lorry number.
 *
 * The haulier fills them in while the load is Confirmed or Dispatched; the
 * customer reads them and cannot change them; the supplier is never sent them
 * at all, so this renders nothing for it.
 *
 * CR-7781: the values entered here are what the haulier's Confirm Load (in
 * the modal footer) rests on, so unsaved edits are reported up to hold it.
 * Once the load has left the supplier the block says why it is read-only
 * rather than failing a save. While its Admin Setup switch is on, the
 * customer may change the cost — and, CR-8613, every other haulage field — in
 * place, with a reason ({@link CallOffCustomerHaulageCost}).
 *
 * CR-9072: the haulier also picks the truck type (Standard / Mega), which the
 * supplier is sent too. On a load in transit the haulier may still fill in a
 * price, truck type or lorry number that is blank (`haulage_fillable_fields`). Those show as inputs in
 * the locked block; the rest stay read-only, and a value once saved locks.
 */
import { useEffect, useState } from 'react';
import { callOffAPI } from '../../../lib/api';
import type { CallOff, HaulageEntryRequest } from '../../../lib/api';
import { getApiErrorMessage } from '../../../lib/api/types/common';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Loader2, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { joinAnd } from '../../../utils/listText';
import CallOffCustomerHaulageCost from './CallOffCustomerHaulageCost';
import TruckTypeSelect from './TruckTypeSelect';
import { truckTypeLabel } from './truckType';

interface CallOffHaulagePanelProps {
  callOff: CallOff;
  /** Reload the call-off and its history after a save. */
  onSaved: () => void;
  /** CR-7781: told when the form gains or loses unsaved edits. */
  onDirtyChange?: (dirty: boolean) => void;
}

interface HaulageForm {
  haulage_cost: string;
  haulier_pickup_date: string;
  haulier_agreed_delivery_date: string;
  lorry_number: string;
  /** CR-9072: a TruckType code, or '' when not set. */
  truck_type: string;
}

function toForm(callOff: CallOff): HaulageForm {
  return {
    haulage_cost: callOff.haulage_cost ?? '',
    haulier_pickup_date: callOff.haulier_pickup_date ?? '',
    haulier_agreed_delivery_date: callOff.haulier_agreed_delivery_date ?? '',
    lorry_number: callOff.lorry_number ?? '',
    truck_type: callOff.truck_type ?? '',
  };
}

const LABELS: Record<keyof HaulageForm, string> = {
  haulage_cost: 'Haulage Cost',
  haulier_pickup_date: 'Pickup Date',
  haulier_agreed_delivery_date: 'Agreed Delivery Date',
  lorry_number: 'Lorry Number',
  truck_type: 'Truck Type',
};

/** The fields shown as dates, in the viewer's format, when read-only. */
const DATE_FIELDS: ReadonlyArray<keyof HaulageForm> = ['haulier_pickup_date', 'haulier_agreed_delivery_date'];

/**
 * The block's order: price and truck as booked, then the dates, then the
 * lorry number, which is only known on the day.
 */
const FORM_ORDER: ReadonlyArray<keyof HaulageForm> = [
  'haulage_cost', 'truck_type', 'haulier_pickup_date', 'haulier_agreed_delivery_date', 'lorry_number',
];

/** How each typed value is typed in; the truck type is a dropdown. */
const INPUTS: Record<
  Exclude<keyof HaulageForm, 'truck_type'>,
  { id: string; type?: string; min?: string; step?: string; maxLength?: number }
> = {
  haulage_cost: { id: 'haulage-cost', type: 'number', min: '0', step: '0.01' },
  lorry_number: { id: 'lorry-number', maxLength: 50 },
  haulier_pickup_date: { id: 'haulier-pickup', type: 'date' },
  haulier_agreed_delivery_date: { id: 'haulier-delivery', type: 'date' },
};

/**
 * The save body: only the values the viewer may write, so a load in transit
 * (CR-9072) never re-sends the ones that have locked.
 *
 * @param form - the values as typed
 * @param fields - the values the viewer may write
 * @returns the haulage endpoint's request
 */
function toRequest(form: HaulageForm, fields: ReadonlyArray<keyof HaulageForm>): HaulageEntryRequest {
  const body: HaulageEntryRequest = {};
  if (fields.includes('haulage_cost')) body.haulage_cost = form.haulage_cost === '' ? null : form.haulage_cost;
  if (fields.includes('haulier_pickup_date')) body.haulier_pickup_date = form.haulier_pickup_date || null;
  if (fields.includes('haulier_agreed_delivery_date')) {
    body.haulier_agreed_delivery_date = form.haulier_agreed_delivery_date || null;
  }
  if (fields.includes('lorry_number')) body.lorry_number = form.lorry_number;
  if (fields.includes('truck_type')) body.truck_type = form.truck_type;
  return body;
}

/**
 * A saved value as the read-only block shows it.
 *
 * @param field - which value
 * @param value - the saved value (non-empty)
 * @param formatDate - the viewer's date formatter
 * @returns the text to show
 */
function displayValue(field: keyof HaulageForm, value: string, formatDate: (date: string) => string): string {
  if (DATE_FIELDS.includes(field)) return formatDate(value);
  if (field === 'truck_type') return truckTypeLabel(value);
  return value;
}

export default function CallOffHaulagePanel({ callOff, onSaved, onDirtyChange }: CallOffHaulagePanelProps) {
  const { formatDate } = useFormatters();
  const [form, setForm] = useState<HaulageForm>(() => toForm(callOff));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const editable = callOff.can_edit_haulage === true;
  // CR-9072: on a load in transit, the blank price / truck type / lorry number only.
  const fillable: ReadonlyArray<keyof HaulageForm> = editable ? [] : (callOff.haulage_fillable_fields ?? []);
  const writable = editable ? FORM_ORDER : fillable;

  // What the server holds now — the read-only view shows this, not the form,
  // so a value saved from elsewhere (CR-7781: the customer's cost) shows at once.
  const current = toForm(callOff);
  const dirty = writable.some(field => form[field] !== current[field]);

  // CR-7781: the footer's Confirm Load waits for unsaved edits to be saved.
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  // CR-7732: render whatever the server actually sent. It decides who reads
  // what — the supplier is sent the confirmed pickup date (CR-9072: and the
  // truck type), because the collection happens at its site, and none of the
  // rest. Keying the panel on one field would have shown it an empty box or
  // nothing at all.
  const shown = FORM_ORDER.filter(field => field in callOff);
  if (shown.length === 0) return null;

  const customerCanChangeCost = callOff.can_customer_edit_haulage_cost === true;
  const set = (field: keyof HaulageForm, value: string) =>
    setForm(prev => ({ ...prev, [field]: value }));

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await callOffAPI.saveHaulage(callOff.id, toRequest(form, writable));
      toast.success('Haulage details saved');
      onSaved();
    } catch (err: unknown) {
      const message = getApiErrorMessage(err, 'Failed to save haulage details');
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const input = (field: keyof HaulageForm) => {
    if (field === 'truck_type') {
      return (
        <div key={field} className="space-y-1">
          <Label htmlFor="truck-type">{LABELS.truck_type}</Label>
          <TruckTypeSelect id="truck-type" value={form.truck_type} onChange={(value) => set('truck_type', value)} />
        </div>
      );
    }
    const { id, ...props } = INPUTS[field];
    return (
      <div key={field} className="space-y-1">
        <Label htmlFor={id}>{LABELS[field]}</Label>
        <Input id={id} {...props} value={form[field]} onChange={(e) => set(field, e.target.value)} />
      </div>
    );
  };

  const readOnly = (field: keyof HaulageForm) => (
    <div key={field}>
      <label className="block text-sm font-medium text-muted-foreground mb-1">
        {LABELS[field]}
      </label>
      <div className="border border-border rounded-lg bg-muted/30 px-3 py-2 font-medium">
        {current[field]
          ? displayValue(field, current[field], formatDate)
          : <span className="text-muted-foreground">Not entered</span>}
      </div>
    </div>
  );

  const saveRow = (
    <>
      {error && <p role="alert" className="text-xs text-red-600">{error}</p>}
      <div className="flex justify-end">
        <Button size="sm" onClick={save} disabled={saving || !dirty}>
          {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
          Save haulage details
        </Button>
      </div>
    </>
  );

  return (
    <div className="card p-4 border rounded-lg">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-medium">Haulage</h3>
        {callOff.haulier_name && (
          <span className="text-sm text-muted-foreground">{callOff.haulier_name}</span>
        )}
      </div>

      {editable ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            {FORM_ORDER.map(input)}
          </div>
          {saveRow}
        </div>
      ) : (
        <div className="space-y-3">
          {/* CR-7781: say why the block cannot be changed once it has
              locked, rather than leave a read-only box to be mistaken for a
              broken one. Not for a customer that may still change the cost —
              "locked" beside a Change button would contradict itself. */}
          {callOff.haulage_lock_reason && !customerCanChangeCost && (
            <p className="flex items-center gap-1.5 rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
              <Lock className="h-3.5 w-3.5 shrink-0" />
              <span>
                {callOff.haulage_lock_reason}
                {/* CR-9072: and what the haulier may still add. */}
                {fillable.length > 0
                  && ` You can still add the ${joinAnd(fillable.map(field => LABELS[field].toLowerCase()))}.`}
              </span>
            </p>
          )}
          <div className="grid grid-cols-2 gap-3">
            {shown.map(field => {
              // CR-7781 / CR-8613: the customer changes any of them in
              // place, with a reason.
              if (customerCanChangeCost) {
                return <CallOffCustomerHaulageCost key={field} field={field} callOff={callOff} onSaved={onSaved} />;
              }
              return fillable.includes(field) ? input(field) : readOnly(field);
            })}
          </div>
          {fillable.length > 0 && saveRow}
        </div>
      )}
    </div>
  );
}
